import store from "./store";
import {tgxApi} from "./tgxApi";

const toQueryString = (query: Record<string, any>) => {
    const params = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
        if (value === null || value === undefined) {
            return;
        }

        params.set(key, Array.isArray(value) ? value.join(",") : String(value));
    });

    return params.toString();
};

export const utilityRequestsApi = tgxApi.injectEndpoints({
    endpoints: (build) => ({
        calculateEditorValue: build.mutation({
            query: ({calculation, requestBody, national = false}) => ({
                url: `/editor/auto-calculated-values/${national ? "national/" : ""}${calculation}`,
                method: "POST",
                body: requestBody,
            }),
        }),

        calculateWithExemptionEstimate: build.mutation({
            query: ({taxYear, requestBody}) => ({
                url: `/editor/auto-calculated-values/withExemptionEstimate/${taxYear || 0}`,
                method: "POST",
                body: requestBody,
            }),
        }),

        calculateCurrentWoExemptAmount: build.mutation({
            query: ({taxYear, requestBody}) => ({
                url: `/editor/auto-calculated-values/currentWoExemptAmount/${taxYear || 0}`,
                method: "POST",
                body: requestBody,
            }),
        }),

        populateStateAndCounty: build.mutation({
            query: ({collectorId, isNeedCountyList = false}) => ({
                url: `/ajax/populate-state-county/${collectorId}/${isNeedCountyList}`,
                method: "GET",
            }),
        }),

        estimateTotalCalculatedDue: build.mutation({
            query: (requestBody) => ({
                url: "/ajax/estimate-total-calculated-due",
                method: "POST",
                body: requestBody,
            }),
        }),

        getActiveOrdersByCustomer: build.query({
            query: (customerId) => ({
                url: `/ajax/orders/${customerId}`,
                method: "GET",
            }),
        }),

        getOrderBarChart: build.mutation({
            query: (query) => ({
                url: `/ajax/orders-bar-chart?${toQueryString(query)}`,
                method: "POST",
            }),
        }),

        getAutomatedOrderPercentBarChart: build.mutation({
            query: (query) => ({
                url: `/ajax/automated-orders-percent-bar-chart?${toQueryString(query)}`,
                method: "POST",
            }),
        }),

        submitImportedOrder: build.mutation({
            query: (requestBody) => ({
                url: "/ajax/submit/order",
                method: "POST",
                body: requestBody,
            }),
            invalidatesTags: ["orders"],
        }),

        fetchBlobObjectUrl: build.mutation<string, string>({
            query: (url) => ({
                url,
                method: "GET",
                headers: {
                    "Cache-Control": "no-cache",
                    Pragma: "no-cache",
                    Accept: "*/*",
                },
                responseHandler: async (response: Response) => {
                    if (!response.ok) {
                        throw new Error("Failed to fetch file");
                    }

                    const blob = await response.blob();
                    return URL.createObjectURL(blob);
                },
            }),
        }),

        downloadPaymentTemplate: build.mutation<Blob, void>({
            query: () => ({
                url: "/ajax/emptyCsvFile/paymentFile",
                method: "GET",
                responseHandler: async (response: Response) => {
                    if (!response.ok) {
                        throw new Error("Download failed");
                    }

                    return response.blob();
                },
            }),
        }),
    }),
});

export const dispatchUtilityRequest = async <T = any>(
    endpoint: keyof typeof utilityRequestsApi.endpoints,
    args?: any
): Promise<T> => {
    const actionCreator = utilityRequestsApi.endpoints[endpoint] as any;
    const result = store.dispatch(actionCreator.initiate(args));

    try {
        return await result.unwrap();
    } finally {
        result.unsubscribe?.();
    }
};

export const {
    useGetActiveOrdersByCustomerQuery,
    useGetOrderBarChartMutation,
    useGetAutomatedOrderPercentBarChartMutation,
    useSubmitImportedOrderMutation,
    useFetchBlobObjectUrlMutation,
    useDownloadPaymentTemplateMutation,
} = utilityRequestsApi;
